<template>
  <div class="data-table-condensed">
    <q-table
      class="data-table-condensed__table"
      title-class="data-table-condensed__table--title"
      dense
      :title="title + ` @ ` + this.lastUpdated"
      :data="rows"
      no-data-label="No Data"
      no-results-label="The filter didn't uncover any results"
      :columns="columns"
      :pagination.sync="pagination"
      hide-pagination
      row-key="name"
      :key="columns.name"
      flat
      style="height: 735px"
      :loading="visible"
    >
      <template v-slot:loading>
        <q-inner-loading showing color="secondary" />
      </template>
      <template v-slot:header-cell="props">
        <q-th :props="props">
          <span v-if="props.col.label === 'Variance'">
            {{ props.col.label }}
            <span class="inline-tooltip">
              <q-icon name="help" color="secondary" size="15px">
                <q-tooltip content-class="tooltip">
                  <p class="q-mb-xs">
                    <strong>Variance</strong> = (Today values so far - Average
                    of the last 3 months equivalent days*) / average of last 3
                    months equivalent days*
                  </p>
                  <p class="q-mb-xs">
                    *Equivalent days are based on the day of month, for example
                    if today is 2nd Tuesday in month, then calculation is based
                    on the last 3 months 2nd Tuesdays
                  </p>
                  <ul class="tooltip__legend">
                    <li>
                      <div class="key red" />
                      <strong>Red</strong>: value less than -5%
                    </li>
                    <li>
                      <div class="key amber" />
                      <strong>Amber</strong>: value between -5% and 5%
                    </li>
                    <li>
                      <div class="key green" />
                      <strong>Green</strong>: value greater than 5%
                    </li>
                  </ul></q-tooltip
                ></q-icon
              >
            </span>
          </span>
          <span v-else> {{ props.col.label }}</span>
        </q-th>
      </template>
      <template v-slot:body-cell="props">
        <q-td :props="props">
          <span
            v-if="props.col.label === 'Variance'"
            class="variance"
            :class="checkVariance(props.value)"
            >{{ formatPercentage(props.value) }}</span
          >
          <span v-else> {{ props.value }}</span>
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script>
import { formatPercentage } from "@/utils/formatPercentage";
import { count } from "@/utils/formatNumbers";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatDateTime } from "@/utils/formatDate";
import { formatTextLabel } from "@/utils/formatText";

export default {
  props: {
    title: String,
    dataset: String,
    timer: Number,
    filter: Object,
  },

  data() {
    return {
      pagination: {
        page: 1,
        rowsPerPage: 26,
      },
      columns: [],
      keys: [],
      rows: [],
      errors: [],
      interval: null,
      dataRefresh: this.$props.timer,
      visible: true,
      lastUpdated: "Loading...",
    };
  },

  methods: {
    formatPercentage,
    async fetchData(siteId, networkId, operatorId) {
      // Dataset name is the store procedure name used to return the results.
      const payload = {
        filterOptions: {
          siteIds: siteId ? [siteId] : [],
          networkId,
          operatorId,
        },
        report: "today-statistics",
        dataset: this.$props.dataset.replace(/ /g, ""),
      };

      await this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          if (response.data[0] !== undefined) {
            const keys = Object.keys(response.data[0]);
            this.columns = keys.map((key) => ({
              name: key,
              label: formatTextLabel(key),
              field: key,
              row: key,
              sortable: true,
              align:
                key == "Today" || key == "Average" || key == "Variance"
                  ? "right"
                  : "left",
              format: (value, index) => {
                if (key === "StatName" && value !== null) {
                  // TYPE
                  return index.StatName;
                } else if (key === "Today" && value !== null) {
                  // TODAY
                  if (
                    index.StatName === "Registrations" ||
                    index.StatName === "FTDs" ||
                    index.StatName === "Cash Players" ||
                    index.StatName === "Bonus Players" ||
                    index.StatName === "Depositing Players" ||
                    index.StatName === "Withdrawing Players" ||
                    index.StatName === "# Deposits"
                  ) {
                    return count(index.Today);
                  } else if (
                    index.StatName === "FTD Rate" ||
                    index.StatName === "Wager as Cash" ||
                    index.StatName === "Margin" ||
                    index.StatName === "NGR/Deposits"
                  ) {
                    if(index.StatName==="Wager as Cash"){
                      index.StatName = "Stake as Cash";
                    }
                    // Percentage
                    return formatPercentage(index.Today);
                  } else if (
                    index.StatName === "FTD Amount" ||
                    index.StatName === "Deposits" ||
                    index.StatName === "Withdrawals" ||
                    index.StatName === "Net Cash" ||
                    index.StatName === "Bonus Awarded" ||
                    index.StatName === "Bonus Converted" ||
                    index.StatName === "Total Wagering" ||
                    index.StatName === "Cash Wager" ||
                    index.StatName === "GGR" ||
                    index.StatName === "NGR"
                  ) {
                    if(index.StatName==="Total Wagering"){
                      index.StatName = "Total Stake";
                    }else if(index.StatName === "Cash Wager"){
                      index.StatName = "Cash Stake";
                    }
                    // Currency - Rounded
                    return formatCurrency(index.Today);
                  } else if (
                    index.StatName === "Avg. FTD Amount" ||
                    index.StatName === "Avg. Deposit" ||
                    index.StatName === "Avg. Deposits/Player" ||
                    index.StatName === "Avg. Cash Wager"
                  ) {
                    if(index.StatName === "Avg. Cash Wager"){
                      index.StatName = "Avg. Cash Stake"
                    }
                    // Currency
                    return formatCurrency(index.Today);
                  } else {
                    return index.Today;
                  }
                } else if (key === "Average" && value !== null) {
                  // AVERAGE
                  if (
                    index.StatName === "Registrations" ||
                    index.StatName === "FTDs" ||
                    index.StatName === "Cash Players" ||
                    index.StatName === "Bonus Players" ||
                    index.StatName === "Depositing Players" ||
                    index.StatName === "Withdrawing Players" ||
                    index.StatName === "# Deposits"
                  ) {
                    return count(index.Average);
                  } else if (
                    index.StatName === "FTD Rate" ||
                    index.StatName === "Wager as Cash" ||
                    index.StatName === "Margin" ||
                    index.StatName === "NGR/Deposits"
                  ) {
                    if(index.StatName === "Wager as Cash"){
                      index.StatName = "Stake as Cash"
                    }
                    // Percentage
                    return formatPercentage(
                      index.Average
                    );
                  } else if (
                    index.StatName === "FTD Amount" ||
                    index.StatName === "Deposits" ||
                    index.StatName === "Withdrawals" ||
                    index.StatName === "Net Cash" ||
                    index.StatName === "Bonus Awarded" ||
                    index.StatName === "Bonus Converted" ||
                    index.StatName === "Total Wagering" ||
                    index.StatName === "Cash Wager" ||
                    index.StatName === "GGR" ||
                    index.StatName === "NGR"
                  ) {
                    if(index.StatName==="Total Wagering"){
                      index.StatName = "Total Stake";
                    }else if(index.StatName === "Cash Wager"){
                      index.StatName = "Cash Stake";
                    }
                    // Currency - Rounded
                    return formatCurrency(index.Average);
                  } else if (
                    index.StatName === "Avg. FTD Amount" ||
                    index.StatName === "Avg. Deposit" ||
                    index.StatName === "Avg. Deposits/Player" ||
                    index.StatName === "Avg. Cash Wager"
                  ) {
                    if(index.StatName === "Avg. Cash Wager"){
                      index.StatName = "Avg. Cash Stake"
                    }
                    // Currency
                    return formatCurrency(index.Average);
                  } else {
                    return index.Average;
                  }
                } else if (key === "Variance" && value !== null) {
                  // VARIANCE
                  return index.Variance;
                } else {
                  return `---`;
                }
              },
            }));
          }
          this.rows = response.data;
          this.visible = false;
        })
        .catch((e) => {
          this.errors.push(e);
        });

      // Also get last updated date.
      payload.dataset = "TodayStatistics_LastUpdated";
      await this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          this.lastUpdated = formatDateTime(
            response.data[0].LastUpdatedDate
          );
        })
        .catch((e) => {
          this.errors.push(e);
        });
    },
    checkVariance(value) {
      // Check value of argument and assign colour
      if (value < -5) {
        return "red";
      }
      if (value >= -5 && value <= 5) {
        return "amber";
      }
      if (value > 5) {
        return "green";
      }
    },
  },
  watch: {
    filter: function(filterOption) {
      this.fetchData(
        filterOption.siteId,
        filterOption.networkId,
        filterOption.operatorId
      );

      this.visible = true;

      clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.fetchData(
          filterOption.siteId,
          filterOption.networkId,
          filterOption.operatorId
        );
      }, 180000);
    },
    rows: function() {
      this.checkVariance();
    },
  },
  mounted() {
    this.fetchData(
      this.$store.state.siteFilter.id,
      this.$store.state.networkFilter.id,
      this.$store.state.selectedOperator.id
    );
    this.visible = true;
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      this.fetchData(
        this.$store.state.siteFilter.id,
        this.$store.state.networkFilter.id,
        this.$store.state.selectedOperator.id
      );
    }, 180000);
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.rows.length / this.pagination.rowsPerPage);
    },
  },
  beforeDestroy: function() {
    clearInterval(this.interval);
  },
};
</script>

<style lang="scss">
.inline-tooltip {
  font-weight: 100;
  font-size: 10px;
  margin-left: 2px;
}
.tooltip {
  border-collapse: separate;
  border-spacing: 0;
  empty-cells: show;
  border: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(50px);
  border-radius: 5px;
  text-align: left;
  font-size: 11px;
  overflow: hidden;
  color: white;
  font-family: Barlow;
  font-style: normal;
  font-size: 12.7397px;
  &__legend {
    margin-left: -35px !important;
    li {
      display: flex;
      align-items: center;
      margin-bottom: 6px;
      .key {
        display: block;
        height: 10px;
        width: 10px;
        border-radius: 50%;
        margin-right: 8px;
      }
      .red {
        background: #ff0000 !important;
      }
      .amber {
        background: #f39c00 !important;
      }
      .green {
        background: #00c908 !important;
      }
    }
  }
}
</style>
